import { createContext, type PropsWithChildren, useContext, useMemo } from "react";
import {
  type PaymentProviderResponse,
  useAllPaymentProvidersQuery,
  usePaymentProviderQuery,
} from "@/api/hooks/usePaymentProvider.ts";
import { useAppConfig } from "@/dataProviders/ConfigDataProvider.tsx";
import type { SelectOptions } from "@/modules/domesticPayments/types.ts";
import { sortCountries, sortCountriesByCountryAndName } from "@/lib/sortCountries.ts";
import { getNameFromCountryCode } from "@/lib/getCountryName.ts";

export type BankInfo = Exclude<PaymentProviderResponse["paymentProviders"], undefined>;
export type BankToConnectOptions = { key: string; label: string; value: string; img: string }[];

type BanksData = {
  unstructuredData: PaymentProviderResponse["paymentProviders"];
  sortedUnstructuredData: PaymentProviderResponse["paymentProviders"];
  banksByCountry: Record<string, SelectOptions>;
  filterBanksByCountry: Record<string, BankToConnectOptions>;
  bankData: Record<string, BankInfo[number]>;
  sortiedBankCountries: string[];
  codesOptions: SelectOptions;
  countryOptions: SelectOptions;
  isPending: boolean;
  isError: boolean;
};

const banksDefault = {
  unstructuredData: [],
  sortedUnstructuredData: [],
  banksByCountry: {},
  filterBanksByCountry: {},
  bankData: {},
  sortiedBankCountries: [],
  codesOptions: [],
  countryOptions: [],
  isPending: false,
  isError: false,
};

function BankDataProviderInner({
  children,
  data,
  isPending,
  isError,
}: PropsWithChildren<{ data: PaymentProviderResponse | undefined; isPending: boolean; isError: boolean }>) {
  const unstructuredData = data?.paymentProviders;
  const sortedUnstructuredData = sortCountriesByCountryAndName(unstructuredData);

  const { banksByCountry, bankData, codesOptions, filterBanksByCountry } = useMemo(() => {
    const banksByCountry: Record<string, SelectOptions> = {};
    const filterBanksByCountry: Record<string, BankToConnectOptions> = {};
    const bankData: Record<string, BankInfo[number]> = {};
    const codesOptions: SelectOptions = [];
    if (!data?.paymentProviders) return { banksByCountry, bankData, codesOptions, filterBanksByCountry };

    for (const provider of data.paymentProviders) {
      if (!provider.countryCode) continue;
      if (!provider.providerCode) continue;

      if (!banksByCountry[provider.countryCode]) {
        banksByCountry[provider.countryCode] = [];
      }
      banksByCountry[provider.countryCode].push({
        key: provider.providerCode ?? "",
        value: provider.providerCode ?? "",
        label: provider.name ?? "",
      });
      if (!filterBanksByCountry[provider.countryCode]) {
        filterBanksByCountry[provider.countryCode] = [];
      }
      if (provider.settledSupport === true || provider.pairingSupport === true) {
        filterBanksByCountry[provider.countryCode].push({
          key: provider.providerCode ?? "",
          value: provider.providerCode ?? "",
          label: provider.name ?? "",
          img: provider.logoUrl ?? "",
        });
      }

      if (bankData[provider.providerCode]) {
        bankData[provider.providerCode] = {};
      }
      bankData[provider.providerCode] = {
        name: provider.name,
        providerCode: provider.providerCode,
        countryCode: provider.countryCode,
        logoUrl: provider.logoUrl,
      };

      codesOptions.push({
        key: provider.providerCode,
        value: provider.providerCode,
        label: provider.providerCode ?? "",
      });
    }

    const sortedCodesOptions = [...codesOptions];
    sortedCodesOptions.sort((a, b) => {
      return a.value.localeCompare(b.value);
    });
    const sortedBanksByCountries: Record<string, SelectOptions> = {};
    for (const key in banksByCountry) {
      if (key in banksByCountry) {
        sortedBanksByCountries[key] = [...banksByCountry[key]].sort((a, b) => a.value.localeCompare(b.value));
      }
    }

    return { banksByCountry: sortedBanksByCountries, bankData, codesOptions: sortedCodesOptions, filterBanksByCountry };
  }, [data]);

  const sortiedBankCountries = sortCountries(Object.keys(banksByCountry));
  const countryOptions: SelectOptions = sortCountries(Object.keys(banksByCountry)).map((country) => ({
    key: country,
    value: country,
    label: `${getNameFromCountryCode(country)} - ${country}`,
  }));

  const value = useMemo(
    () => ({
      unstructuredData,
      sortedUnstructuredData,
      banksByCountry,
      filterBanksByCountry,
      bankData,
      sortiedBankCountries,
      codesOptions,
      countryOptions,
      isPending,
      isError,
    }),
    [
      bankData,
      banksByCountry,
      filterBanksByCountry,
      codesOptions,
      countryOptions,
      isError,
      isPending,
      sortedUnstructuredData,
      sortiedBankCountries,
      unstructuredData,
    ],
  );

  return <BankCtx.Provider value={value}>{children}</BankCtx.Provider>;
}

export function BankDataProvider({ children }: PropsWithChildren) {
  const { activeApp } = useAppConfig();

  const { data, isPending, isError } = usePaymentProviderQuery(
    { pageNumber: 0, pageSize: 100 },
    { merchantId: activeApp },
  );

  return (
    <BankDataProviderInner data={data} isPending={isPending} isError={isError}>
      {children}
    </BankDataProviderInner>
  );
}

export function AllBankDataProvider({ children }: PropsWithChildren) {
  const { data, isPending, isError } = useAllPaymentProvidersQuery();

  return (
    <BankDataProviderInner data={data} isPending={isPending} isError={isError}>
      {children}
    </BankDataProviderInner>
  );
}

export const BankCtx = createContext<BanksData>(banksDefault);

export const useBankData = () => useContext(BankCtx);
